import React, {useEffect} from "react";
import { Cross1Icon, CheckCircledIcon, LinkBreak2Icon } from "@radix-ui/react-icons";
import { Icon } from '@src/components/icon/Icon';
import { CustomTooltip } from '../tooltip/Tooltip';
import { v4 as uuidv4 } from 'uuid';
import { useSpring, animated } from '@react-spring/web';
import { usePreviewPlacement } from './usePreviewPlacement';

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface Option {
  value: string
  label: string
}

export interface ValueRendererProps {
  value: Option[]
  options?: Option[]
  onValueChange: (options: Option[]) => void
  children?: React.ReactNode
  disabled?: boolean
}
const DefaultValueRenderer = ({ value, onValueChange, options, children }: ValueRendererProps) => {

  return (
    <div className="w-full flex flex-wrap items-center">
      {(value || []).map((option) => {
        const isValueValid = options?.find((opt) => opt.value === option.value);
        return (
          <Badge className="mr-2 my-1 px-1 max-w-[200px]" variant="secondary" key={option.label}>
            <CustomTooltip content={isValueValid ? option.label : 'The associated object was deleted'}>
              <div className={cn("truncate font-light", { 'pl-4': !isValueValid })}>
                {!isValueValid && <LinkBreak2Icon className="absolute text-red-500 left-1"/>}
                {option.label}
              </div>
            </CustomTooltip>
            <CustomTooltip content="Remove FitFact">
              <Cross1Icon
                className={cn(
                  "ml-1 cursor-pointer max-w-3 min-w-3",
                )}
                onClickCapture={() => {
                  onValueChange?.((value || []).filter((opt) => opt.value !== option.value));
                }}
              />
            </CustomTooltip>
          </Badge>
        )}
      )}
      {children}
    </div>
  );
}

export interface OptionRendererProps {
  isValueSelected: boolean
  option: Option
  disabled: boolean
}
const DefaultOptionRenderer = ({ isValueSelected, option, disabled }: OptionRendererProps) => {
  return (
    <div className="flex items-center relative w-full">
      <div className="w-6 min-w-6">
        {isValueSelected && <CheckCircledIcon/>}
      </div>
      <div className="max-w-full truncate">
        {option.label}
      </div>
      {isValueSelected && !disabled && (
        <CustomTooltip content="Remove option">
          <Cross1Icon
            className={cn(
              "ml-auto z-10 r-0 cursor-pointer max-w-3 min-w-3",
            )}
          />
        </CustomTooltip>
      )}
    </div>
  )
};

export interface MessageComboboxProps {
  value?: Option[]
  options?: Option[]
  withCustomOptions?: boolean
  ignoredValues?: string[]
  hideSelectedOptions?: boolean
  shouldCloseOnSelect?: boolean
  disabled?: boolean,
  addBtnLabel?: string,
  filterPlaceholder?: string,
  onValueChange?: (value: Option[], extraOpts?: Record<string, unknown>) => void,
  popoverContentOverrides?: Record<string, unknown>,
  onOpenChange?: (value: boolean) => void,
  Renderer?: (props: ValueRendererProps) => React.ReactNode
  OptionRenderer?: (props: OptionRendererProps) => React.ReactNode
  // LF360-2015: drawn beside the list for the row under the pointer or the
  // arrow keys, once it has stayed there for PREVIEW_DELAY_MS
  renderPreview?: (optionValue: string) => React.ReactNode
}

// long enough that sweeping the pointer down the list previews (and fetches) nothing
export const PREVIEW_DELAY_MS = 150;
const PREVIEW_KEYS = new Set(['ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown']);

export const MessageCombobox = React.forwardRef<HTMLDivElement, MessageComboboxProps>(function MessageCombobox({
  value = [],
  options,
  withCustomOptions = false,
  hideSelectedOptions = false,
  shouldCloseOnSelect = false,
  ignoredValues,
  disabled,
  addBtnLabel,
  onValueChange,
  filterPlaceholder='Search option...',
  onOpenChange,
  popoverContentOverrides={},
  Renderer = DefaultValueRenderer,
  OptionRenderer = DefaultOptionRenderer,
  renderPreview,
}: MessageComboboxProps, forwardedRef) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("");
  const searchRef = React.useRef<HTMLInputElement>(null);
  // cmdk 0.2 reports the active row only when it is controlled
  const [cmdkValue, setCmdkValue] = React.useState('');
  const [activeValue, setActiveValue] = React.useState<string>();
  const [previewValue, setPreviewValue] = React.useState<string>();
  // cmdk marks the first row active on open; only a pointer move or an arrow
  // key counts as the user pointing at a row
  const hasPointedRef = React.useRef(false);
  const listWrapperRef = React.useRef<HTMLDivElement>(null);
  const { setPreviewEl, style: previewStyle } = usePreviewPlacement(listWrapperRef);
  const [{ opacity, boxShadow }, api] = useSpring(() => ({
    opacity: 0,
    boxShadow: '0px 0px 0px rgba(0, 0, 0, 0)',
    config: { duration: 200 },
  }));

  useEffect(() => {
    if (open) {
      api.set({
        opacity: 0,
        boxShadow: '0px 0px 0px rgba(0, 0, 0, 0)'
      });
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      api.start({
        opacity: 1,
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)'
      });
    } else {
      api.set({
        opacity: 0,
        boxShadow: '0px 0px 0px rgba(0, 0, 0, 0)'
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      hasPointedRef.current = false;
      setCmdkValue('');
      setActiveValue(undefined);
      setPreviewValue(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (!renderPreview) return;
    if (!activeValue) {
      setPreviewValue(undefined);
      return;
    }
    const timer = setTimeout(() => setPreviewValue(activeValue), PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [activeValue]);

  // cmdk lowercases item values
  const toOptionValue = (value: string) => options?.find((option) => option.value.toLowerCase() === value)?.value;

  const markPointed = () => {
    if (hasPointedRef.current) return;
    hasPointedRef.current = true;
    // the row cmdk already marked sends no change, so pointing at it must count
    if (renderPreview) setActiveValue(toOptionValue(cmdkValue));
  };

  const handleActiveChange = (nextCmdkValue: string) => {
    setCmdkValue(nextCmdkValue);
    if (!renderPreview || !hasPointedRef.current) return;
    setActiveValue(toOptionValue(nextCmdkValue));
  };

  const preview = renderPreview && previewValue ? renderPreview(previewValue) : null;

  const canAddOption =
    search.length > 0
    && (value || []).filter(() => {
      return (value || []).some(option => option.label.toLowerCase() === search.toLowerCase())
    }).length === 0;
  const areCustomOptionsAllowed = withCustomOptions && !options;

  const searchChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }

  const handleAddOption = () => {
    const optionToSelect = options?.find((option) => option.label.toLowerCase().includes(search.toLowerCase()));
    if (options && optionToSelect) {
      onValueChange?.(
          [...(value || []), optionToSelect]
      );
      setSearch('');
      searchRef.current?.focus();
    } else if (areCustomOptionsAllowed) {
      const uuid = uuidv4();
      onValueChange?.(
          [...(value || []), { value: uuid, label: search }]
      );
      setSearch('');
      searchRef.current?.focus();
    }
  };

  const valueWithoutOptions = (value || []).filter((option) => !options?.some((opt) => opt.value === option.value));

  return (
    <div>
      <Popover
        open={open}
        onOpenChange={(value) => {
          setOpen(value);
          onOpenChange?.(value);
        }}
      >
        <Renderer value={value} onValueChange={onValueChange!} options={options} disabled={disabled}>
          {!disabled && (
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                role="combobox"
                aria-expanded={open}
                disabled={disabled}
                className="justify-between px-2"
              >
                <Icon type="plus" />
                {addBtnLabel}
              </Button>
            </PopoverTrigger>
          )}
        </Renderer>
        <PopoverContent
          className={cn("w-[200px] p-0 bg-transparent shadow-none")}
          {...popoverContentOverrides}
          ref={forwardedRef}
        >
          <animated.div
            ref={listWrapperRef}
            className="relative"
            style={{
              opacity: opacity,
              boxShadow: boxShadow
            }}
          >
            <Command
                value={cmdkValue}
                onValueChange={handleActiveChange}
                onKeyDownCapture={(e) => {
                  if (PREVIEW_KEYS.has(e.key)) markPointed();
                }}
                filter={(value, search) => {
                  const relatedOption = options?.find((option) => option.value === value);
                  if (value === "option-add" && search && canAddOption) return 1
                  if (relatedOption?.label.toLowerCase().includes(search.toLowerCase())) return 1
                  return 0
                }}
            >
                <CommandInput
                    ref={searchRef}
                    value={search}
                    onChangeCapture={searchChangeHandler}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && canAddOption) {
                        e.preventDefault();
                        handleAddOption();
                      }
                    }}
                    placeholder={filterPlaceholder}
                    className="h-9"
                />
                <CommandGroup
                    className="max-h-[400px] overflow-y-auto overscroll-contain"
                    // capture, so the flag is set before cmdk reacts to the same move
                    onPointerMoveCapture={markPointed}
                >
                  {[...(options ?? []), ...(hideSelectedOptions ? [] : valueWithoutOptions)].map((option) => {
                    const isValueSelected = (value || []).some((opt) => opt.value === option.value);
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={(currentValue) => {
                          (!isValueSelected)
                            ? onValueChange?.([...(value || []), option])
                            : onValueChange?.((value || []).filter((option) => option.value !== currentValue));
                          if (shouldCloseOnSelect) {
                            setOpen(false);
                            onOpenChange?.(false);
                          } else {
                            searchRef.current?.focus();
                          }
                        }}
                      >
                        <OptionRenderer isValueSelected={isValueSelected} option={option} disabled={disabled ?? false}/>
                      </CommandItem>
                    );
                  })}
                  {search && !ignoredValues?.includes(search) && areCustomOptionsAllowed && (
                    <CommandItem key="option-add" value="option-add">
                      <Button
                        variant="link"
                        className="w-full flex justify-center"
                        disabled={disabled}
                        onClickCapture={handleAddOption}
                      >
                        Add option
                      </Button>
                    </CommandItem>
                  )}
                </CommandGroup>
            </Command>
            {preview && (
              <div ref={setPreviewEl} className="absolute" style={previewStyle}>
                {preview}
              </div>
            )}
          </animated.div>
        </PopoverContent>
      </Popover>
    </div>
)
});
